import { tt } from '@agos/shared'

/**
 * Whether push notifications can actually deliver — FB-C03.
 *
 * Measured on this stack: `GET /api/v1/push/public-key` answers `{"publicKey": ""}` — no VAPID key is
 * configured — while `customer_push_subscriptions` holds **114** rows. So 114 browsers are registered
 * for a channel that cannot send.
 *
 * The backend is careful about this. Its controller says an empty answer *"is how the frontend learns
 * push is not configured here"*, and `bulk-push` reports what it *could* have reached rather than
 * claiming a send, because *"a screen that says 'sent to 400' when nothing left the building is worse
 * than one that says nothing was sent."*
 *
 * The customer app is not. `getVapidPublicKey` turns the empty string into `null` so `enablePush` fails
 * correctly — but `getPushState` never asks for the key at all, deciding `granted-active` purely from
 * `pushManager.getSubscription()`. Every browser that subscribed before the key went missing is told
 * push is working.
 *
 * So `unconfigured` is a state here, distinct from *denied* and from *on*, and the banner does not offer
 * to enable a channel that cannot deliver.
 */

export type PushState =
  | 'unsupported'
  /**
   * An iPhone or iPad in a browser tab - §9.734, FB-1036, owner O104.
   *
   * Safari outside a home-screen app has neither `Notification` nor `PushManager`, so this browser cannot
   * subscribe - but the same phone can, from iOS 16.4, once the page is added to the home screen and opened from
   * there. So it is not `unsupported`, which says *this browser cannot*, and the row says what to do instead.
   */
  | 'home-screen'
  | 'unconfigured'
  /**
   * Nobody is signed in - §9.648, FB-976.
   *
   * The save is `@RequireCustomerAuth` and the row is keyed to a customer, so there is nothing anonymous to
   * store. A state rather than a check at the banner, because the profile's push row reads this too and would
   * otherwise say *Vypnuté* to somebody who cannot switch it on.
   */
  | 'signed-out'
  | 'denied'
  | 'default'
  | 'granted-active'
  | 'granted-inactive'

export function hasVapidKey(publicKey: unknown): boolean {
  return typeof publicKey === 'string' && publicKey.trim() !== ''
}

/**
 * The VAPID key as the server actually sends it.
 *
 * `GET /push/public-key` answers `{ "publicKey": "" }` on this data — a 200 with an empty string, not
 * a 404 — so the payload is well-formed and the key is absent. Reading it has to keep those two apart:
 * `null` here means *the server did not answer with a key*, and an empty string means *it did, and
 * there is none configured*. Both end up as "cannot deliver", but only after {@link hasVapidKey} has
 * said so, and that is the check legacy never makes.
 *
 * **Beside `hasVapidKey` since §9.239**, because the profile needs it as much as the banner does and a
 * package cannot import a module. `customer-notifications` re-exports this one rather than keeping a
 * second copy.
 */
export function publicKeyOf(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null
  const value = (payload as { publicKey?: unknown }).publicKey
  return typeof value === 'string' ? value : null
}

/**
 * The state, from the four things that decide it.
 *
 * The key is checked before the browser's own answers, deliberately: a browser that supports push, has
 * granted permission and holds a subscription still receives nothing if nothing can sign a message.
 */
export function pushState(input: {
  supported: boolean
  /**
   * Whether this is an iPhone or iPad tab that could push from the home screen - §9.734, {@link homeScreenOnly}.
   * Required for §9.648's reason: both callers know, and a default would be a guess about the customer's phone.
   */
  homeScreenOnly: boolean
  publicKey: unknown
  /** Whether a customer is signed in - §9.648. Required, because every caller knows and a default would guess. */
  signedIn: boolean
  permission: 'granted' | 'denied' | 'default'
  hasSubscription: boolean
}): PushState {
  /* §9.734 - the device is asked only once the browser has said it cannot: an iPhone that runs the page from the
   * home screen supports push and goes on through the ordinary states below. */
  if (!input.supported) return input.homeScreenOnly ? 'home-screen' : 'unsupported'
  if (!hasVapidKey(input.publicKey)) return 'unconfigured'
  /* §9.648 - **after the key and before the permission**, deliberately. The key is first because a channel
   * that cannot deliver is not worth a prompt whoever is looking; the session is next because asking the
   * browser for permission and then failing to save is the worst order available - it spends the one prompt a
   * browser gives you and leaves the permission granted with nothing subscribed. */
  if (!input.signedIn) return 'signed-out'
  if (input.permission === 'denied') return 'denied'
  if (input.permission === 'default') return 'default'
  return input.hasSubscription ? 'granted-active' : 'granted-inactive'
}

/** Whether offering to switch push on can lead anywhere. */
export function mayEnable(state: PushState): boolean {
  /* §9.648 - `signed-out` is not in this list, which is what hides the banner from an anonymous visitor.
     Legacy's own one-line gate, ported: `if (hidden || !user) return null`. */
  return state === 'default' || state === 'granted-inactive'
}

/**
 * What the profile's push row is — §9.239, FB-397.
 *
 * The row was a **statement**: a fixed *Zapnuté/Vypnuté* label, on the argument that a checkbox which
 * sometimes opens a permission dialog and sometimes does nothing is worse than a fact. That argument
 * is right for `unconfigured` — this stack's own state, where no VAPID key exists and enabling cannot
 * work whatever the customer presses — and wrong for `granted-active`, where switching **off** needs
 * no prompt, always succeeds, and is the one action a consent screen has to offer. Legacy has had the
 * switch since the port; AGOS had the endpoint and no caller.
 *
 * `denied` draws the box disabled rather than hiding it, as legacy does: a customer who blocked
 * notifications in the browser should see a row saying so.
 */
export interface PushToggle {
  /** Whether to draw a checkbox at all, as against the sentence alone. */
  shown: boolean
  checked: boolean
  disabled: boolean
  /** What pressing it does, or `null` when it does nothing. */
  action: 'enable' | 'disable' | null
}

/*
 * §9.734 - **`pushToggle` and `pushNote` are exhaustive `switch`es with no `default`**, so a state added to
 * `PushState` is TS2366 in both rather than a fall-through to an offer. §9.648 below found what the if-chains
 * they replace did with a new state; `home-screen` is the first state this form caught.
 */
export function pushToggle(state: PushState, busy = false): PushToggle {
  switch (state) {
    // Nothing to offer: the browser cannot, the server has no key to sign with, or an iPhone tab that cannot
    // subscribe until the page runs from the home screen (§9.734) - a box there would do nothing when pressed.
    case 'unsupported':
    case 'home-screen':
    case 'unconfigured':
      return { shown: false, checked: false, disabled: true, action: null }
    case 'denied':
      return { shown: true, checked: false, disabled: true, action: null }
    /* §9.648 - **not offered when nobody is signed in.** Found by reading these two rather than by the
     * typecheck: both were if-chains with a final `return`, so adding a state compiled silently and fell
     * through to `action: 'enable'` - the switch offered to somebody whose save would 401. §9.502's class, and
     * the reason this entry's own first draft claiming TypeScript would name every reader was wrong. */
    case 'signed-out':
      return { shown: false, checked: false, disabled: true, action: null }
    case 'granted-active':
      return { shown: true, checked: true, disabled: busy, action: 'disable' }
    case 'default':
    case 'granted-inactive':
      return { shown: true, checked: false, disabled: busy, action: 'enable' }
  }
}

export function pushNote(state: PushState): string {
  switch (state) {
    case 'granted-active':
      return tt('Upozornenia máte zapnuté.')
    case 'unsupported':
      return tt('Tento prehliadač upozornenia nepodporuje.')
    case 'home-screen':
      /* §9.734 - an instruction, because this one the customer can act on: from iOS 16.4 a page added to the home
       * screen receives push (the app's manifest is `standalone` and its service worker handles `push`). Named in
       * Safari's own words - *Zdieľať*, *Pridať na plochu* - since that is where the customer will look. */
      return tt(
        'Na iPhone a iPade upozornenia fungujú len z ikony na ploche - v Safari ťuknite na Zdieľať → Pridať na plochu a potom otvorte stránku z plochy.',
      )
    case 'unconfigured':
      // Said plainly rather than as an error: it is the restaurant's configuration, not the customer's
      // browser, and there is nothing they can do about it.
      return tt('Upozornenia zatiaľ nie sú spustené - reštaurácia ich ešte nemá nastavené.')
    case 'denied':
      return tt('Upozornenia sú zablokované - povoliť sa dajú v nastaveniach prehliadača.')
    /* §9.648 - says what to do about it, because unlike `unconfigured` this one the customer can fix. The
     * two states below are an offer, and offering a subscription that cannot be saved is the defect. */
    case 'signed-out':
      return tt('Upozornenia vieme zapnúť po prihlásení - predplatné patrí k vášmu kontu.')
    case 'default':
    case 'granted-inactive':
      return tt('Chcete vedieť, keď je objednávka na ceste?')
  }
}

// ─── §9.734: the device, and what the restaurant still sends to ─────────────────

/**
 * The four facts about a device that decide whether push needs the home-screen app - §9.734.
 *
 * Gathered by {@link readDevice}; a plain value so the rule below is a pure function a test can hand any phone.
 */
export interface DeviceFacts {
  userAgent: string
  platform: string
  maxTouchPoints: number
  /** Running as an installed app: `display-mode: standalone`, or iOS's own `navigator.standalone`. */
  standalone: boolean
}

/**
 * An iPhone, iPod or iPad - **including iPadOS reporting itself as a Mac.** Since iPadOS 13 Safari asks for the
 * desktop site by default, so the user agent says `Macintosh` and `navigator.platform` says `MacIntel`; the touch
 * screen is what gives it away, because a real Mac reports `maxTouchPoints` 0.
 */
export function isAppleTouchDevice(
  device: Pick<DeviceFacts, 'userAgent' | 'platform' | 'maxTouchPoints'>,
): boolean {
  if (/iPhone|iPod|iPad/.test(device.userAgent)) return true
  return device.platform === 'MacIntel' && device.maxTouchPoints > 1
}

/**
 * The iOS version the user agent states, or `null`.
 *
 * An iPhone's agent says `OS 17_4`; the iPadOS desktop agent says `Mac OS X 10_15_7`, which is no iOS version at
 * all, so its Safari `Version/17.4` is read instead - on an iPad the two move together.
 */
export function appleOsVersion(userAgent: string): { major: number; minor: number } | null {
  const mobile = / OS (\d+)_(\d+)/.exec(userAgent)
  if (mobile !== null) return { major: Number(mobile[1]), minor: Number(mobile[2]) }
  const safari = /Version\/(\d+)\.(\d+)/.exec(userAgent)
  return safari === null ? null : { major: Number(safari[1]), minor: Number(safari[2]) }
}

/**
 * Whether push on this device needs the page added to the home screen - the `home-screen` state's test.
 *
 * An Apple touch device, **not** already running as the installed app, on iOS 16.4 or later - Apple's first
 * release with web push, and only for home-screen apps. An older iOS cannot receive it from the home screen either,
 * so it keeps the general *nepodporuje* sentence rather than an instruction that leads nowhere. An unknown version
 * is taken as able, which is what nearly every iPhone in use is.
 */
export function homeScreenOnly(device: DeviceFacts): boolean {
  if (!isAppleTouchDevice(device) || device.standalone) return false
  const version = appleOsVersion(device.userAgent)
  if (version === null) return true
  return version.major > 16 || (version.major === 16 && version.minor >= 4)
}

const NO_DEVICE: DeviceFacts = { userAgent: '', platform: '', maxTouchPoints: 0, standalone: false }

/**
 * This device's facts, read defensively: never throws, and safe where there is no `window`.
 *
 * `navigator.platform` is deprecated and still the one field that tells an iPad asking for the desktop site from a
 * Mac; nothing replaces it in Safari, which does not implement `userAgentData`.
 */
export function readDevice(): DeviceFacts {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return NO_DEVICE
    const standalone =
      (typeof window.matchMedia === 'function' &&
        window.matchMedia('(display-mode: standalone)').matches) ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      maxTouchPoints: navigator.maxTouchPoints,
      standalone,
    }
  } catch {
    return NO_DEVICE
  }
}

/**
 * How many browsers the server holds for this customer, from `GET /customer-auth/me/push-subscriptions` - or
 * `null` when the read has not answered with one.
 *
 * The writer is `CustomerProfileSectionDtos.subscriptionsOf`: `{ subscriptions: [...], count: n }`.
 */
export function subscriptionCountOf(payload: unknown): number | null {
  if (typeof payload !== 'object' || payload === null) return null
  const count = (payload as { count?: unknown }).count
  return typeof count === 'number' && Number.isFinite(count) && count >= 0 ? count : null
}

/**
 * Whether this browser's subscription is one the restaurant still sends to - §9.734, on §10A.381.
 *
 * `pushManager.getSubscription()` answers for the **browser**; every push is sent to the **server's** rows. After
 * the manager removes a customer's subscriptions (§10A.381) the browser still holds its own, so a browser-only answer
 * went on saying *Upozornenia máte zapnuté* while nothing was sent. So: a browser that holds a subscription counts as
 * subscribed unless the server answered **0** rows for this customer. An unanswered read keeps the browser-only
 * answer, which is today's; a subscribe made in this page counts at once, so the row does not flicker while the
 * count refetches.
 *
 * It cannot tell one of two devices from the other - the read carries no endpoint - which is no worse than before.
 */
export function subscribedHere(input: {
  browserHolds: boolean
  serverCount: number | null
  registeredHere: boolean
}): boolean {
  if (!input.browserHolds) return false
  if (input.registeredHere) return true
  return input.serverCount === null || input.serverCount > 0
}

// ─── §9.737: when the offer opens ─────────────────────────────────────────────────

/**
 * Which offer the push dialog makes: switch push on here, or (an iPhone or iPad tab) how to get it from the home
 * screen - §9.737, owner O107.
 */
export type PushPrompt = 'offer' | 'home-screen'

/** Everything that decides whether the offer opens. Every field is required, so a caller cannot forget one. */
export interface PushPromptFacts {
  state: PushState
  /** A customer is signed in. The offer saves a subscription keyed to them, and remembers their answer. */
  signedIn: boolean
  /**
   * The restaurant has a VAPID key. The offer's own states imply one - {@link pushState} answers `unconfigured`
   * without it - but `home-screen` is decided before the key is read, so the iPhone variant asks here.
   */
  keyConfigured: boolean
  /**
   * The browser's subscription probe and the server's list have both answered (or the list is not being asked).
   * Before that the state is a guess, and a customer who is already subscribed would see the offer flash.
   */
  settled: boolean
  /** The customer answered *Nie* for this account on this device. */
  declined: boolean
  /**
   * The customer closed the offer for this visit: in this tab, within the last two hours - or another tab of this
   * device switched push on for them, which this tab's own copy of the browser cannot see until it reloads.
   */
  closedThisTab: boolean
  /**
   * Another modal dialog is on the page (§9.737, corrected after review). The offer opens on its own schedule - when
   * the browser and the server have answered - not on a press, so without this it could open underneath the lunch
   * menu, the wheel or the branch picker, which all sit higher, with the keyboard's focus on a close button nobody
   * can see. It waits for that dialog to close instead.
   */
  anotherDialogOpen: boolean
}

/**
 * Whether the push offer opens, and which one - or `null` for not now.
 *
 * **Opens only when all of these hold** (§9.737 point 2): a customer is signed in; the state is one push can be
 * switched on from (`mayEnable`) or an iPhone tab that could from the home screen, with a key to send with; the
 * probe and the list have answered; no *Nie* is on record for this customer; it has not been closed for this visit;
 * and no other modal dialog is on the page.
 *
 * **The cookie question is not one of them** (corrected after review). The first draft waited for it, reasoning
 * that *"the consent dialog is modal too"* - and it is not: `CookieConsentBanner`'s own docstring calls the card
 * *"non-blocking until the customise panel is opened"*, so a customer can use the whole site without answering it,
 * and for as long as they did the offer never opened. The owner asked for it *"always when logged in and they not
 * used No"*. The offer's backdrop sits above the card's, so the card waits dimmed behind it rather than competing.
 *
 * **Not opened** (point 6) for `signed-out`, `unsupported`, `unconfigured` (an offer that cannot deliver),
 * `denied` (the browser's own *Block* is already a no, and the account row says how to undo it) and
 * `granted-active`. On every route, because the dialog that asks this sits above the router.
 */
export function pushPromptOf(facts: PushPromptFacts): PushPrompt | null {
  if (!facts.signedIn) return null
  if (!facts.settled) return null
  if (facts.declined) return null
  if (facts.closedThisTab) return null
  if (facts.anotherDialogOpen) return null
  if (mayEnable(facts.state)) return 'offer'
  /* An iPhone tab is told how to get push - owner question #149, answered by §9.737 point 5 - but only where the
     restaurant can send it: following the instruction with no key would end on `unconfigured`, which point 6 does
     not offer. */
  if (facts.state === 'home-screen' && facts.keyConfigured) return 'home-screen'
  return null
}

/** The base64url VAPID key as the subscription API wants it - one decoding, shared with the courier's app (§9.786). */
export { vapidKeyBytes } from '@agos/shared'
